import 'regenerator-runtime/runtime';
import { configureZodInvalidValueErrors } from '@erp/shared-types';
import 'src/index.css';
import { initSentry } from 'src/integrations/sentry/sentry';
import { initDemoCompanyNameReplacer } from 'src/lib/demoCompanyNameReplacer';
import { StrictMode, useContext } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthContext, AuthProvider } from 'src/context/auth.context';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from 'src/api/queryClient';
import { MaterialUIControllerProvider } from 'src/md-context';
import { ToastProvider } from 'src/context/ToastContext';
import { AnalyticsProvider } from 'src/context/analytics.context';
import { GTProvider, T } from 'gt-react';
import gtConfig from '../gt.config.json';
import loadTranslations from 'src/loadTranslations';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { routeTree } from './routes';
import { useIsModuleEnabled } from './hooks/organization-config/useOrganizationEnabledModules';
import { AppSkeleton } from './AppSkeleton';
import { PageSkeleton } from './components/common/loading-skeletons/page-skeleton';
import { Button } from 'src/components/ui/button';
import { authzClient } from 'src/context/authz/AuthzClient';
import {
  AlertContainer,
  AlertTitle,
  AlertDescription,
} from 'src/components/ui/alert';
import { AuthServiceErrorState } from 'src/pages/(unauthenticated)/AuthServiceErrorState';
import { UpdateRequiredOverlay } from 'src/components/common/UpdateRequiredOverlay';

// Install the global Zod error map before any schema parsing runs.
configureZodInvalidValueErrors();
initSentry();
initDemoCompanyNameReplacer();

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  // The app scrolls inside the authenticated layout's <main>, not the window,
  // so window-based scroll restoration alone never resets it. This scrolls the
  // real scroller back to top on forward navigations (back/forward still
  // restore the saved position).
  scrollToTopSelectors: ['#app-main-content'],
  defaultPendingComponent: PageSkeleton,
  defaultPendingMs: 200,
  defaultPendingMinMs: 300,
  context: {
    // TanStack Router pattern: auth is initialized as a placeholder here and
    // replaced with the real context when RouterProvider mounts (see line 119).
    // Per https://tanstack.com/router/latest/docs/framework/react/guide/router-context
    // oxlint-disable-next-line no-non-null-assertion
    auth: undefined!,
    queryClient,
    isModuleEnabled: () => false,
    authzClient,
  },
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function RouteProviderWithContext() {
  const auth = useContext(AuthContext);
  const { isModuleEnabled, isLoading: isModulesLoading } = useIsModuleEnabled();

  if (auth.errorLoadingUser && !auth.currentUser) {
    return (
      <AuthServiceErrorState
        error={auth.errorLoadingUser}
        isRetrying={auth.isRefetchingUser}
        onRetry={() => {
          void auth.refetchUser();
        }}
      />
    );
  }

  if (auth.isFetchingAuthInitialStatus) {
    return null;
  }

  if (isModulesLoading) {
    return <AppSkeleton />;
  }

  return (
    <>
      {auth.errorLoadingUser && auth.currentUser && (
        <div className="fixed top-0 left-0 right-0 z-50 p-3">
          <AlertContainer variant="destructive" className="shadow-lg">
            <AlertTitle>
              <T context="auth error alert title">Something Went Wrong</T>
            </AlertTitle>
            <AlertDescription>
              <T context="auth error alert description">
                We&apos;re having trouble connecting. Please try again or
                contact support.
              </T>
            </AlertDescription>
            <div className="flex gap-2 mt-3 col-start-2">
              <Button
                size="sm"
                onClick={() => auth.refetchUser()}
                disabled={auth.isRefetchingUser}
              >
                {auth.isRefetchingUser ? (
                  <T context="auth error retry button loading">Retrying...</T>
                ) : (
                  <T context="auth error retry button">Try Again</T>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.location.assign('/login')}
              >
                <T context="auth error back to login button">Back to Login</T>
              </Button>
            </div>
          </AlertContainer>
        </div>
      )}
      <RouterProvider
        router={router}
        context={{ auth, queryClient, isModuleEnabled }}
      />
    </>
  );
}

const rootElement = document.getElementById('root');
if (rootElement) {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <GTProvider config={gtConfig} loadTranslations={loadTranslations}>
        <QueryClientProvider client={queryClient}>
          <MaterialUIControllerProvider>
            <ToastProvider>
              <AuthProvider>
                <AnalyticsProvider>
                  <RouteProviderWithContext />
                </AnalyticsProvider>
              </AuthProvider>
              <UpdateRequiredOverlay />
            </ToastProvider>
          </MaterialUIControllerProvider>
        </QueryClientProvider>
      </GTProvider>
    </StrictMode>,
  );
}
